import { computed, inject } from '@angular/core';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  applyCallbackToAttributes,
  combineAttributeValuesInCategories,
  DefaultColorValues,
  extractCustomColorsFromMap,
  generateAttributeCategories,
  getAllAttributes,
  getAllPagesData,
  groupGraphicIdsAndAttributesByKey,
  sortCategories,
  toAttributeTreeStructure,
  updateNoValueInAllRanges,
} from '@bizzdesign/modeler/utils-modeler';
import { getSinglePageOrThrow } from '@bizzdesign/sdk-knowledgegraph';
import type {
  AttributeCategoryDefinitionDto,
  AttributeCustomColors,
  AttributeStateCategory,
  AttributeTreeNode,
  AttributeValueColor,
  AttributeValueColorMap,
  GraphicIdParam,
  ObjectDto,
  RepositoryLoadedElementIdPair,
  SelectedAttributeWithRangeValue,
} from '@bizzdesign/shared/models';
import { KnowledgeGraphClientBDCore } from '@bizzdesign/shared/services';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, from, pipe, switchMap } from 'rxjs';

type AttributeState = {
  categories: AttributeStateCategory[];
  selectedAttribute: AttributeTreeNode | undefined;
  selectedAttributeValueColors: AttributeValueColorMap | undefined;
  customColors: string[];
  customColorsMap: AttributeCustomColors | undefined;
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[];
};

const initialState: AttributeState = {
  categories: [],
  selectedAttribute: undefined,
  selectedAttributeValueColors: undefined,
  customColors: [],
  customColorsMap: undefined,
  attributeCategoryDefinitions: [],
};

const GET_OBJECTS_ATTRIBUTES_PAGE_SIZE = 50;

export const AttributeLocalStore = signalStore(
  { protectedState: false },
  withState(initialState),
  withComputed(({ categories }) => ({
    treeCategories: computed(() => toAttributeTreeStructure(categories())),
  })),
  withMethods((store) => {
    const knowledgeGraphClient = inject(KnowledgeGraphClientBDCore);

    const getObjectsCall = (
      fromRepoIdPairs: RepositoryLoadedElementIdPair[],
    ): Promise<ObjectDto[]> => {
      const objectCall = knowledgeGraphClient.getObjects({
        filter: {
          ids: fromRepoIdPairs.map(
            (ids: RepositoryLoadedElementIdPair) => ids.semanticId,
          ) as UUID[],
        },
      });
      const objects = getAllPagesData<ObjectDto>(
        objectCall,
        GET_OBJECTS_ATTRIBUTES_PAGE_SIZE,
      );
      return objects;
    };

    const updateAttributeCategoryDefinitions = rxMethod<void>(
      pipe(
        switchMap(() =>
          from(
            getSinglePageOrThrow(
              knowledgeGraphClient
                .getAttributeCategoryDefinitions()
                .asPages({ pageSize: 250 }),
            ),
          ).pipe(
            tapResponse({
              next: (attributeCategoryDefinitions) => {
                patchState(store, {
                  attributeCategoryDefinitions,
                });
              },
              error: (error) => {
                console.log(error);
              },
            }),
          ),
        ),
      ),
    );

    function updateSelectedAttribute() {
      const currentSelectedAttribute = store.selectedAttribute();
      if (!!!currentSelectedAttribute) return;

      const possibleAttributes = store
        .treeCategories()
        .map((node) => node.children?.map((childNode) => childNode.children))
        .flat(2);

      const updatedSelectedAttribute = possibleAttributes.find(
        (node) => node?.key === currentSelectedAttribute.key,
      ) as AttributeTreeNode;

      if (!!!updatedSelectedAttribute) {
        patchState(store, {
          selectedAttribute: undefined,
          selectedAttributeValueColors: undefined,
        });
        return;
      }
      patchState(store, {
        selectedAttribute: {
          ...currentSelectedAttribute,
          range: [...(updatedSelectedAttribute?.range ?? [])],
        },
      });
    }
    return {
      updateAttributeCategoryDefinitions,
      updateSelectedAttribute,
      getObjectsData: rxMethod<{
        fromRepoIdPairs: RepositoryLoadedElementIdPair[];
        allGraphicIds: string[];
      }>(
        pipe(
          filter(({ fromRepoIdPairs }) => !!fromRepoIdPairs.length),
          switchMap(({ fromRepoIdPairs, allGraphicIds }) =>
            from(getObjectsCall(fromRepoIdPairs)).pipe(
              tapResponse({
                next: (response) => {
                  const categories = generateAttributeCategories(
                    response,
                    store.attributeCategoryDefinitions(),
                  );

                  const allAttributes = getAllAttributes(
                    response,
                    fromRepoIdPairs,
                  );

                  const groupedGraphicIdsAndAttributesByKey =
                    groupGraphicIdsAndAttributesByKey(allAttributes);

                  const combinedValuesWithCategories =
                    applyCallbackToAttributes(
                      categories,
                      (category, attribute) =>
                        combineAttributeValuesInCategories(
                          category,
                          attribute,
                          allGraphicIds,
                          groupedGraphicIdsAndAttributesByKey,
                          store.attributeCategoryDefinitions(),
                        ),
                    );

                  patchState(store, {
                    categories: combinedValuesWithCategories,
                  });

                  updateSelectedAttribute();
                },
                error: (err) => {
                  console.log(err);
                },
              }),
            ),
          ),
        ),
      ),
      setSelectedAttribute: (
        selectedAttribute: AttributeTreeNode | undefined,
      ) => {
        patchState(store, {
          selectedAttribute,
          selectedAttributeValueColors: undefined,
          customColors: [],
          customColorsMap: undefined,
        });
      },
      setSelectedAttributeValueColors: (
        selectedAttributeValueColors: AttributeValueColorMap | undefined,
      ) => {
        patchState(store, { selectedAttributeValueColors });
      },
      setCustomColorMap: (
        key: string | undefined,
        value: AttributeValueColor,
      ) => {
        if (!key) return;
        const currentCustomColors = store.customColorsMap();
        const currentCustomColorsForKey = currentCustomColors?.[key];
        const baseCustomColorsForKey =
          currentCustomColorsForKey ?? DefaultColorValues;

        const { color, isCustom, type } = value;
        const newColorInMap = { [type]: { color, isCustom } };

        const newCustomColorsForKey = {
          ...baseCustomColorsForKey,
          ...newColorInMap,
        };

        const customColorsMap = {
          ...currentCustomColors,
          [key]: newCustomColorsForKey,
        };

        const customColors = extractCustomColorsFromMap(
          store.customColors(),
          customColorsMap,
          key,
        );

        patchState(store, { customColorsMap, customColors });
      },
      resetState: () => {
        patchState(store, initialState);
      },
      updateNoValueInCategories: (graphicIds: GraphicIdParam[]) => {
        const updatedCategories = applyCallbackToAttributes(
          store.categories(),
          (category, attribute) =>
            updateNoValueInAllRanges(category, attribute, graphicIds),
        );

        const sortedCategories = sortCategories(updatedCategories);

        patchState(store, {
          categories: sortedCategories,
        });
        updateSelectedAttribute();
      },
      toggleRangeValueVisibility: (e: SelectedAttributeWithRangeValue) => {
        const withChangedVisibility = {
          ...e.rangeValue,
          isVisible: !e.rangeValue.isVisible,
        };

        const updatedSelectedAttribute = {
          ...e.selectedAttribute,
          range: e.selectedAttribute.range?.map((r) =>
            r.value === withChangedVisibility.value ? withChangedVisibility : r,
          ),
        };

        patchState(store, {
          selectedAttribute: updatedSelectedAttribute,
        });
      },
      toggleColorPickerVisibility: (e: SelectedAttributeWithRangeValue) => {
        const withChangedVisibility = {
          ...e.rangeValue,
          isColorPickerVisible: !e.rangeValue.isColorPickerVisible,
        };

        const updatedSelectedAttribute = {
          ...e.selectedAttribute,
          range: e.selectedAttribute.range?.map((r) =>
            r.value === withChangedVisibility.value ? withChangedVisibility : r,
          ),
        };

        patchState(store, {
          selectedAttribute: updatedSelectedAttribute,
        });
      },
    };
  }),
);

export type AttributeLocalStore = InstanceType<typeof AttributeLocalStore>;
